// Return-to-page after sign-in.
//
// AuthGuard records the /app page an unauthenticated visitor asked for (for
// example a texted link to /app/verify), and LoginView sends her
// there after a successful sign-in instead of to /app.
//
// Only same-app relative paths under /app are ever stored or honored, so a
// crafted value cannot turn this into an open redirect: no scheme, no
// protocol-relative "//host", no backslashes (browsers treat "/\host" like
// "//host"), no whitespace or control characters.
export const POST_LOGIN_REDIRECT_KEY = 'cp.postLoginRedirect';

const MAX_PATH_LENGTH = 2000;

export const isSafeAppPath = (path) => {
  if (typeof path !== 'string' || !path || path.length > MAX_PATH_LENGTH) {
    return false;
  }
  // Exactly "/app", or "/app" followed by a path, query or fragment.
  if (!/^\/app(?:[/?#]|$)/.test(path)) {
    return false;
  }
  if (path.includes('//') || path.includes('\\')) {
    return false;
  }
  // Whitespace and control characters.
  // eslint-disable-next-line no-control-regex
  if (/[\s\u0000-\u001f\u007f]/.test(path)) {
    return false;
  }
  return true;
};

export const storePostLoginRedirect = (pathname, search = '') => {
  const path = `${pathname || ''}${search || ''}`;
  if (!isSafeAppPath(path)) {
    return;
  }
  try {
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, path);
  } catch (e) {
    // sessionStorage unavailable -- sign-in falls back to /app.
  }
};

export const clearPostLoginRedirect = () => {
  try {
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  } catch (e) {
    // Nothing to clear.
  }
};

// Read and remove the stored path. Returns it only when it is still a safe
// /app path; otherwise null, and the caller uses its default.
export const consumePostLoginRedirect = () => {
  let path = null;
  try {
    path = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
  } catch (e) {
    return null;
  }
  clearPostLoginRedirect();
  return isSafeAppPath(path) ? path : null;
};
